import axiosInstance from "../../../interceptors/token";
import { API_BASE_URL } from "../../../constants";
import translateText from "../../../util";
import { calculateTransportTime } from "./projectDetailTransportTime";

export const fetchProject = async (id, selectedDate, language, setFormData, setProject, setProjectOrder, setUserWorkEntry, setFiles, setExtraFiles, setTranslatedDescription, setTranslatedDetailedDescription, setCopyOptions) => {
        try {
            const projectResponse = await axiosInstance.get(`${API_BASE_URL}projects/${id}/`);
            const projectWorkersResponse = await axiosInstance.get(`${API_BASE_URL}projects/${id}/workers/?date=${selectedDate}`)
            const projectOrderResponse = await axiosInstance.get(`${API_BASE_URL}projects/order/`, {params: {date: selectedDate}})
            const projectExtraFilesAmountResponse = await axiosInstance.get(`${API_BASE_URL}projects/${id}/dropbox/project/amount/`)
            const projectOrder = projectOrderResponse.data.project_order;
            const project_extra_files_amount = projectExtraFilesAmountResponse.data.file_count
            
            setProject({
                ...projectResponse.data,
                workers: projectWorkersResponse.data
            });
            
            setProjectOrder(projectOrder);
            setExtraFiles(project_extra_files_amount > 0 ? [] : null);

            translateDescription(projectResponse.data.description, language, setTranslatedDescription);
            translateDescription(projectResponse.data.detailed_description, language, setTranslatedDetailedDescription);

            // Reset states before fetching new data
            setUserWorkEntry(null);
            setFormData({
                preparation: "0.0",
                start_time: "",
                break: "0.0",
                end_time: "",
                transport: "0.0",
                warehouse: "0.0",
                materials: "",
                materialen: [], // 27/10/2026 (Bartel): materialen gestructureerd
                description_work: "",
                finished: undefined,
                photos: []
            });


            let userEntries = [];
            try {
                const workResponse = await axiosInstance.get(`${API_BASE_URL}projects/${id}/werk/?date=${selectedDate}`);
                userEntries = workResponse.data;
            } catch (error) {
                if (error.response?.status === 404) {
                    console.log("No user delivery note found for this date.");
                } else {
                    console.error("Error fetching user delivery note:", error);
                }
            }

            // Fetch timing data
            let timingData = null;
            try {
                const timingResponse = await axiosInstance.get(`${API_BASE_URL}projects/${id}/werk/timing/`, { params: { date: selectedDate } });
                timingData = timingResponse.data;
                
            } catch (error) {
                if (error.response?.status === 404) {
                    console.log("No timing data found for this date.");
                } else {
                    console.error("Error fetching timing data:", error);
                }
            }

            // Update states based on fetched data
            if (userEntries.length > 0) {
                console.log(userEntries[0]);
                
                setUserWorkEntry({
                    ...userEntries[0],
                    start_time: timingData?.start_time || "",
                    end_time: timingData?.end_time || "",
                });

                try {
                    const photosResponse = await axiosInstance.get(`${API_BASE_URL}projects/${id}/dropbox/worker/`, { params: { date: selectedDate } });
                    setFiles(photosResponse.data.files || []);
                } catch (error) {
                    console.error("Error fetching photos:", error);
                }
            } else {
                // Calculate transport time
                const transportTime = await calculateTransportTime(projectOrder, parseInt(id), selectedDate, timingData?.start_time, 0);

                setFormData((prev) => ({
                    ...prev,
                    transport: transportTime,
                    start_time: timingData?.start_time || "",
                    end_time: timingData?.end_time || "",
                }));
                setFiles([]);
                const filledInResponse = await axiosInstance.get(`${API_BASE_URL}projects/${id}/filled_in/`, {
                    params: { date: selectedDate },
                });
                setCopyOptions(filledInResponse.data.data);
            }
            
            fetchExtraFiles(id, setExtraFiles);


        } catch (error) {
            console.error("Error fetching project details:", error);
        }
};

// De bestanden van voorafDocs (de lijst "Extra Project Files"). Apart, zodat de lijst ook
// opnieuw geladen kan worden nadat een werker een ondertekend exemplaar terugzette (22/10/2026).
export const fetchExtraFiles = async (id, setExtraFiles) => {
    try {
        const extraFilesResponse = await axiosInstance.get(
            `${API_BASE_URL}projects/${id}/dropbox/project/`
        );
        const files = extraFilesResponse.data.files || [];
        setExtraFiles(files);
    } catch (error) {
        console.error("Error fetching extra files:", error);
        // 27/10/2026 (Bartel): niets mag stil mislukken; de lijst toont dan een foutmelding.
        setExtraFiles({ error: true });
    }
};

// 27/10/2026 (Bartel, "voorafDocs sneller"): de lijst komt zonder inhoud; één bestand halen we pas op
// als de werker erop tikt (tijdelijke Dropbox-link), foto's tonen eerst als kleine thumbnail.
export const getProjectFileLink = async (id, path) => {
    const response = await axiosInstance.get(
        `${API_BASE_URL}projects/${id}/dropbox/project/file/`,
        { params: { path } }
    );
    return response.data; // { url, name }
};

// De bytes van een pdf (om te tekenen). Eerst rechtstreeks via de tijdelijke link; lukt dat niet
// (bv. de browser weigert het), dan via de backend zelf (?raw=1).
export const getProjectFileBytes = async (id, path) => {
    try {
        const { url } = await getProjectFileLink(id, path);
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Dropbox link failed: ${response.status}`);
        return new Uint8Array(await response.arrayBuffer());
    } catch (error) {
        console.warn("Temporary link failed, falling back to backend download:", error);
        const response = await axiosInstance.get(
            `${API_BASE_URL}projects/${id}/dropbox/project/file/`,
            { params: { path, raw: 1 }, responseType: "arraybuffer" }
        );
        return new Uint8Array(response.data);
    }
};

// Een <img src> kan geen Authorization-kop meesturen; daarom halen we de thumbnail met de
// axios-instantie (JWT) op en maken we er een blob-url van. De aanroeper geeft die later vrij.
export const getProjectThumbUrl = async (id, path) => {
    const response = await axiosInstance.get(
        `${API_BASE_URL}projects/${id}/dropbox/project/thumb/`,
        { params: { path }, responseType: "blob" }
    );
    return URL.createObjectURL(response.data);
};

// 22/10/2026 (Bartel): een pdf uit voorafDocs (bv. het RAMI-mandaat) wordt op de gsm ondertekend in
// Acrobat; het getekende bestand komt terug als "<naam>-ondertekend.pdf" (sinds 26/10/2026 in de
// projectmap zelf, niet meer in voorafDocs, op vraag van Bartel). De backend
// bepaalt de naam (signed_upload_link), de upload zelf gaat rechtstreeks naar Dropbox.
export const uploadSignedCopy = async (id, originalName, file) => {
    const linkResponse = await axiosInstance.post(
        `${API_BASE_URL}projects/${id}/dropbox/project/signed_upload_link/`,
        { filename: originalName }
    );
    const { upload_link } = linkResponse.data;
    const dropboxResponse = await fetch(upload_link, {
        method: "POST",
        headers: { "Content-Type": "application/octet-stream" },
        body: file,
    });
    if (!dropboxResponse.ok) {
        throw new Error(`Dropbox upload failed: ${dropboxResponse.statusText}`);
    }
    // 30/10/2026 (Bartel): staat het getekende MANDAAT in Dropbox, dan gaat de waarschuwing "mandaat tekenen"
    // uit. Pas na de gelukte upload, nooit ervoor. Mislukt deze melding, dan blijft de waarschuwing gewoon staan.
    if (/(mandat|mandaat|assigner)/i.test(originalName || "")) {
        try {
            await axiosInstance.post(`${API_BASE_URL}projects/${id}/mandaat_getekend/`);
        } catch (e) {
            console.error("Mandaatvlag niet gewist:", e);
        }
    }
};

// 30/10/2026: enkel de projectrij opnieuw lezen (na een getekend mandaat verdwijnt zo de waarschuwing).
export const fetchProjectOnly = async (id) => {
    const response = await axiosInstance.get(`${API_BASE_URL}projects/${id}/`);
    return response.data;
};

const translateDescription = async (description, targetLanguage, setDescriptionFunction) => {
    const translated = await translateText(description, targetLanguage);
    setDescriptionFunction(translated);
};

// 27/10/2026 (Bartel, workflow "dringende interventie"): keuzelijst voor de omschrijving van materialen.
export const fetchMateriaalTypes = async () => {
    const response = await axiosInstance.get(`${API_BASE_URL}materiaal_types/`);
    return response.data || [];
};

// 27/10/2026 (Bartel, workflow "dringende interventie"): werkbon of ingevuld mandaat laten maken door
// de backend. Zonder source: "Werkbon RenoPerfect <datum> - ingevuld.pdf"; met source (naam van een
// mandaat-pdf in voorafDocs): het mandaat met de uitgevoerde werken als bijlage erachter. Het
// resultaat staat in voorafDocs; antwoord { name, path, warnings }.
export const createWerkbon = async (id, { lang, source } = {}) => {
    const body = { lang: lang || "nl" };
    if (source) body.source = source;
    const response = await axiosInstance.post(`${API_BASE_URL}projects/${id}/werkbon/`, body);
    return response.data;
};
